/**
 * CNPJ numérico e alfanumérico (Receita Federal, IN RFB 2.229/2024) — espelha o
 * `CnpjValidator` do backend (originei-common).
 *
 * 14 posições: as 12 primeiras são letras A–Z ou dígitos; as 2 últimas (DV) são dígitos.
 * DV no módulo 11, cada caractere valendo o código ASCII menos 48 ('0'–'9' = 0–9,
 * 'A' = 17...). CNPJs só de dígitos seguem válidos. Máscara: XX.XXX.XXX/XXXX-XX.
 *
 * A forma canônica (a que vai para a API e é gravada) é a normalizada: sem máscara e em
 * maiúsculas. Por isso campos de CNPJ não usam `inputMode="numeric"` — o teclado do
 * celular precisa ter letras.
 */

const PESOS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
const PESOS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]

/** Remove a máscara (e qualquer caractere que não seja letra ou dígito) e passa para maiúsculas. */
export const normalizarCnpj = (valor: string | null | undefined): string =>
  (valor ?? '').replace(/[^0-9A-Za-z]/g, '').toUpperCase()

/** Forma de CNPJ normalizado: 12 letras/dígitos + 2 dígitos. Não confere o DV. */
export const formatoCnpjNormalizado = (valor: string): boolean => /^[0-9A-Z]{12}\d{2}$/.test(valor)

const calcularDv = (base: string, pesos: number[]): number => {
  const soma = pesos.reduce((acc, peso, i) => acc + (base.charCodeAt(i) - 48) * peso, 0)
  const resto = soma % 11
  return resto < 2 ? 0 : 11 - resto
}

/** Valida CNPJ com ou sem máscara, numérico ou alfanumérico (minúsculas contam como maiúsculas). */
export const cnpjValido = (valor: string | null | undefined): boolean => {
  const c = normalizarCnpj(valor)
  if (!formatoCnpjNormalizado(c)) return false
  if (/^(\d)\1{13}$/.test(c)) return false // 00000000000000, 11111111111111, …
  const dv1 = calcularDv(c, PESOS_DV1)
  const dv2 = calcularDv(c, PESOS_DV2)
  return c[12] === String(dv1) && c[13] === String(dv2)
}

/**
 * Máscara progressiva de digitação: XX.XXX.XXX/XXXX-XX (aceita colar com máscara). Letras
 * só nas 12 primeiras posições; os 2 DVs só aceitam dígitos.
 */
export const mascararCnpj = (valor: string | null | undefined): string => {
  const c = normalizarCnpj(valor)
  const base = c.slice(0, 12)
  const dv = c.slice(12).replace(/\D/g, '').slice(0, 2)
  const d = base + (base.length === 12 ? dv : '')
  if (d.length <= 2) return d
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
}

/**
 * Exibição: aplica a máscara a um CNPJ completo (14 posições no formato); qualquer outra
 * coisa volta como veio, para não esconder dado sujo.
 */
export const formatarCnpj = (valor: string | null | undefined): string => {
  if (!valor) return ''
  const c = normalizarCnpj(valor)
  return formatoCnpjNormalizado(c) ? mascararCnpj(c) : valor
}

/**
 * Termo de busca que pode ser (prefixo de) CNPJ: só letras/dígitos ASCII e pontuação de
 * máscara, com 3+ caracteres úteis. Espelha `OportunidadeService.buscar` do backend.
 */
export const termoPareceCnpj = (termo: string): boolean => {
  const t = termo.trim()
  return /^[0-9A-Za-z./-]+$/.test(t) && normalizarCnpj(t).length >= 3
}

/** Casa um CNPJ gravado com o termo digitado (contém, ignorando máscara e caixa). */
export const cnpjContemTermo = (cnpj: string | null | undefined, termo: string): boolean => {
  const t = normalizarCnpj(termo)
  return t.length > 0 && normalizarCnpj(cnpj).includes(t)
}
